import {
	createEmptyAddress,
	COUNTRY_OPTIONS,
	type AddressFormValues,
	US_STATE_OPTIONS,
} from '#app/routes/mocra+/_form+/components/addresses/schema.ts'

export type AddressDraft = AddressFormValues

export function createAddressDraft(
	overrides: Partial<AddressDraft> = {},
): AddressDraft {
	return {
		...createEmptyAddress(),
		...overrides,
	}
}

export function sanitizeAddressDraft(draft: AddressDraft): AddressDraft {
	const trimmedStreet = draft.street.trim()
	const trimmedStreet2 = draft.street2?.trim() ?? ''
	const trimmedCity = draft.city.trim()
	const country = draft.country ? draft.country.trim() : ''

	const normalizedState =
		country === 'US' ? normalizeUsState(draft.state) : draft.state.trim()

	return {
		street: trimmedStreet,
		street2: trimmedStreet2,
		city: trimmedCity,
		state: normalizedState,
		postalCode: draft.postalCode.trim(),
		country,
	}
}

export function isAddressDraftEmpty(draft: AddressDraft): boolean {
	const sanitized = sanitizeAddressDraft(draft)
	return (
		sanitized.street.length === 0 &&
		sanitized.city.length === 0 &&
		sanitized.state.length === 0 &&
		sanitized.postalCode.length === 0
	)
}

export function formatAddressSummary(draft: AddressDraft): string[] {
	const sanitized = sanitizeAddressDraft(draft)
	const lines: string[] = []

	const primaryLine = [sanitized.street, sanitized.street2]
		.map((value) => value?.trim())
		.filter(Boolean)
		.join(', ')
	if (primaryLine.length > 0) {
		lines.push(primaryLine)
	}

	const cityState = [sanitized.city, sanitized.state]
		.map((value) => value?.trim())
		.filter(Boolean)
		.join(', ')
	if (cityState.length > 0) {
		lines.push(cityState)
	}

	const postalCountry = [
		sanitized.postalCode.trim(),
		resolveCountryLabel(sanitized.country),
	]
		.filter(Boolean)
		.join(' ')
	if (postalCountry.length > 0) {
		lines.push(postalCountry)
	}

	return lines.length > 0 ? lines : ['No address set']
}

export function resolveCountryLabel(code: string): string {
	return COUNTRY_OPTIONS.find((option) => option.value === code)?.label ?? code
}

function normalizeUsState(value: string): string {
	const trimmed = value.trim().toUpperCase()
	const allowedStates = new Set(
		US_STATE_OPTIONS.map((option) => option.value.toUpperCase()),
	)
	if (!allowedStates.size) return trimmed
	return allowedStates.has(trimmed)
		? (US_STATE_OPTIONS.find((option) => option.value.toUpperCase() === trimmed)
				?.value ?? trimmed)
		: ''
}
